import React, { ComponentState, FC, useCallback, useEffect, useMemo, useState } from "react";
import Toolbar from "composants/datatable/Toolbar";
import { Field } from "composants/form";
import { tw } from "twind";
import { Fa } from "composants/Icon";
import { FilterBarDefinition } from "types/Component";
import { DatatableFocusState } from "types/Focus";
import { Trans, useTranslation } from "react-i18next";
import { ProcessusProvider } from "composants/processus/ProcessusProvider";
import FilterBarComponent from "composants/datatable/FilterBarComponent";
import { Menu } from "composants/DropDown/Menu";
import SatelliteMenu from "composants/satellite/SatelliteMenu";
import { track } from "tracking";
import { Button, NotificationGroup } from "@axin-org/comet";
import { URL_DOWNLOAD } from "customGlobal";
import auth from "auth";
import DragNDropContext from "composants/DragNDropContext";
import SelectFieldsModal from "containers/SelectFieldsModal/SelectFieldsModal";
import classNames from "classnames";
import { FilterBar } from "types/Search";
import HighlightDialog, { HighlightProperties } from "composants/datatable/dialog/HighlightDialog";
import { Pojo } from "types/Galaxy";
import toaster from "composants/toaster/toaster";
import { uuidv4 } from "utils/uuid.utils";
import * as api from "api/datatable";
import { AxiosPromise } from "axios";
import Checkbox from "composants/checkbox/Checkbox";
import { Tooltip } from "@axin-org/comet";
import { downloadInteractiveReportExport } from "api/interactiveReport";
import GroupByDialog from "composants/datatable/dialog/GroupByDialog";
import { GroupByFunctionParameter } from "types/InteractiveReport";
import AggregateDialog from "composants/datatable/dialog/AggregateDialog";
import { AggregateProperties } from "types/InteractiveReport";
import { checkEntityValid } from "utils/entities.utils";
import { DatatableToolbarProps, ExportProps, ExportType, GroupByState } from "./Grid.types";

function isRowDirty(row: Pojo, dirtyRowsMap: Record<string, Partial<Pojo>>): boolean {
  return Object.values(dirtyRowsMap).some(
    (dirty) => dirty.id != null && row.id != null && dirty.id === row.id,
  );
}

function getColumnListToExport(
  selectedColumn: ComponentState[],
  allColumns: ComponentState[],
  groupByState: GroupByState,
): ComponentState[] {
  if (groupByState.columns.length > 0) {
    // Ajout des colonnes dans le group by
    const columnsToExport = allColumns.filter((c: any) => groupByState.columns.includes(c.column));
    return columnsToExport;
  }
  return selectedColumn;
}

async function prepareExportData(
  type: ExportType,
  selectedColumns: ComponentState[],
  allColumns: ComponentState[],
  errorTitle: string,
  sjmoCode: string,
  tableName: string,
  filter: string,
  useTrad: boolean,
  typeComposant: "DATATABLE" | "INTERACTIVE_REPORT",
  groupByState: GroupByState,
  contextKey?: string,
  filterBar?: FilterBar,
  syjPanelId?: string,
) {
  if (selectedColumns.length === 0) {
    toaster.notify({
      id: uuidv4(),
      group: NotificationGroup.DEFAULT,
      title: errorTitle, //t("commun_au_moins_1_champs_export"),
      priority: "NORMAL",
      intent: "DANGER",
      createdAt: new Date().toISOString(),
    });
    return;
  }
  // L'export attend une liste de componentState avec un minimum d'informations.
  const chosenComponents: ComponentState[] = selectedColumns.map((pojo, index) => {
    return {
      column: pojo.column as string,
      label: useTrad ? (pojo.label as string) : pojo.column,
      typeCompo: pojo.typeCompo as string,
      position: index,
      joinTableName: pojo.joinTableName as string,
    } as ComponentState;
  });

  if (groupByState.columns.length > 0) {
    // Ajout des colonnes provenant des fonctions de regroupements
    // ce mapping à pour but de correspondre à ce que le back attend plutot que de modifier le back pour traiter ce cas particulier.
    const functionColumns = groupByState.functionList.map(
      (fn: GroupByFunctionParameter) => fn.column,
    );
    groupByState.functionList.forEach((fn: GroupByFunctionParameter) => {
      chosenComponents.push(
        ...allColumns
          .filter((c: any) => c.column === fn.column)
          .map(
            (c, index) =>
              ({
                column: fn.label as string,
                label: useTrad ? (c.label as string) : c.column,
                typeCompo: c.typeCompo as string,
                position: index,
                joinTableName: c.joinTableName as string,
              }) as ComponentState,
          ),
      );
    });
  }

  if (type === "XLS" && typeComposant === "DATATABLE") {
    track("datatable::export", { type: "xls" });
    await exportData(
      api.callExportXls,
      chosenComponents,
      sjmoCode,
      tableName,
      filter,
      contextKey,
      filterBar,
      syjPanelId,
    );
  } else if (type === "XLS" && typeComposant === "INTERACTIVE_REPORT") {
    track("interactiveReport::export", { type: "xls" });
    await exportInteractiveReportData(
      chosenComponents,
      sjmoCode,
      tableName,
      filter,
      "excel",
      groupByState,
    );
  } else if (type === "PDF" && typeComposant === "DATATABLE") {
    track("datatable::export", { type: "pdf" });
    await exportData(
      api.callExportPdf,
      chosenComponents,
      sjmoCode,
      tableName,
      filter,
      contextKey,
      filterBar,
      syjPanelId,
    );
  } else if (type === "PDF" && typeComposant === "INTERACTIVE_REPORT") {
    track("interactiveReport::export", { type: "pdf" });
    await exportInteractiveReportData(
      chosenComponents,
      sjmoCode,
      tableName,
      filter,
      "pdf",
      groupByState,
    );
  }
}

async function exportData(
  callExportApi: (
    sjmoCode: string,
    tableName: string,
    filter: string,
    columns: ComponentState[],
    contextKey?: string,
    filterBar?: FilterBar,
    syjPanelId?: string,
  ) => AxiosPromise<string>,
  chosenComponents: ComponentState[],
  sjmoCode: string,
  tableName: string,
  filter: string,
  contextKey?: string,
  filterBar?: FilterBar,
  syjPanelId?: string,
) {
  try {
    const res = await callExportApi(
      sjmoCode,
      tableName,
      filter,
      chosenComponents,
      contextKey,
      filterBar,
      syjPanelId,
    );
    let fakeLink = document.createElement("a");
    const host = URL_DOWNLOAD();
    const uri = res.data;

    fakeLink.href = encodeURI(`${host}${uri}${auth.token ? "&access_token=" + auth.token : ""}`);

    fakeLink.click();
  } catch (e) {
    console.error(
      "cannot export data for the ",
      tableName,
      "on the module",
      sjmoCode,
      " with the filter '",
      filter,
      "'",
    );
  }
}

async function exportInteractiveReportData(
  chosenComponents: ComponentState[],
  sjmoCode: string,
  tableName: string,
  filter: string,
  type: "excel" | "pdf",
  groupByState: GroupByState,
) {
  try {
    // labels : on utilise les labels des colonnes sélectionnées à l'export si existant, sinon column simple
    const labels =
      chosenComponents && chosenComponents.length > 0
        ? chosenComponents.map((col) => col.label || col.column)
        : [];

    // On ne gère pas les groupBy/fonctions de regroupement ici, la toolbar standard DATATABLE n'a pas cette logique
    const params = {
      tableName,
      first: 0,
      size: 99999,
      includes: chosenComponents.map((col) => col.column),
      filter: filter,
      groupBy: groupByState,
      aggregates: [],
      breakRows: [],
      labels: labels,
    };

    // "EXCEL" en dur pour signaler le type d'export demandé (adapter si besoin)
    await downloadInteractiveReportExport(params, type);
  } catch (e) {
    console.error(
      "impossible d'exporter le rapport interactif pour la table",
      tableName,
      "du module",
      sjmoCode,
      "avec le filtre",
      filter,
    );
  }
}

const AgTableToolbar: FC<DatatableToolbarProps> = ({
  sjmoCode,
  tableName,
  processOrigine,
  ctrlkey,
  sjpaId,
  focus,
  selectedFocus,
  selectedRows,
  filterable,
  filters,
  filterBar,
  currentFilter,
  lastFilterFetched,
  columnFilter,
  columns,
  gridPadding,
  groupByState,
  defaultOpenBreakRows = false,
  dirtyRows,
  selectAll,
  rowCount,
  selectedNumber,
  filterAllFields,
  hideFilterBar,
  typeComposant,
  highlightColumns,
  highlights = [],
  aggregates,
  isGroupable,
  ctrlKey,
  setFilterAllFields,
  onFilterChange,
  refresh,
  onFocusChange,
  onSaveFocusPerso,
  onRazFocusPerso,
  onSave,
  onDelete,
  setGridPadding,
  setGroupBy,
  onAdd,
  setSelectAll,
  clearColumnFilter,
  onValidateHighlights,
  onDeleteHighlights,
  onClearHighlights,
  onChangeAggregates,
  onAfterSaveProcess,
}) => {
  const { t } = useTranslation();
  const [openBreakRowDialog, setOpenBreakRowDialog] = useState(false);
  const [highlightDialogOpen, setHighlightDialogOpen] = useState(false);
  const [aggregateDialogOpen, setAggregateDialogOpen] = useState(false);
  // Propriétés utilisées pour l'export PDF et XLS
  const [exportProps, setExportProps] = useState<ExportProps>({
    available: [],
    chosen: [],
    type: "XLS",
    showExport: false,
    groupByState: { columns: [], functionList: [] },
  });

  const filterBarDefaultDtFilter = useMemo(() => {
    return focus.find((f) => f.focusId === selectedFocus)?.defaultFilterBarDate;
  }, [focus, selectedFocus]);

  const columnsWithNumber = useMemo(() => {
    return columns.filter((c: any) => c.isNumber && c.compoVisible);
  }, [columns]);

  const handleFilterChange = useCallback(
    (filterBarId: string | null, startDate: string | null, endDate: string | null) => {
      onFilterChange({
        filterBarId: filterBarId ?? filterBar?.filterBarId ?? null,
        startDate: startDate ?? filterBar?.startDate ?? null,
        endDate: endDate ?? filterBar?.endDate ?? null,
        filterBarDefaultDtFilter,
      });
    },
    [
      onFilterChange,
      filterBar?.filterBarId,
      filterBar?.startDate,
      filterBar?.endDate,
      filterBarDefaultDtFilter,
    ],
  );

  const selectionCount = useMemo(() => {
    let local = 0;
    let remote = 0;
    let dirty = 0;
    const dirtyRowsMap = dirtyRows ?? {};
    const dirtyRowsValues = Object.values(dirtyRowsMap);

    if (selectAll && selectedRows.length === 0) {
      local = dirtyRowsValues.filter((row) => row.id === undefined || row.id === null).length;
      remote = Math.max(0, rowCount - local);
      dirty = dirtyRowsValues.filter((row) => row.id != null).length;
      return { local, remote, dirty };
    }

    for (const row of selectedRows) {
      if (!checkEntityValid(row)) continue;
      if (row.id == null) {
        local++;
      } else {
        remote++;
        if (isRowDirty(row, dirtyRowsMap)) {
          dirty++;
        }
      }
    }

    if (!selectAll) {
      for (const dirtyRow of dirtyRowsValues) {
        if (dirtyRow.id != null) {
          const isInSelection = selectedRows.some(
            (row) => checkEntityValid(row) && row.id === dirtyRow.id,
          );
          if (!isInSelection) {
            dirty++;
          }
        }
      }
    }

    return { local, remote, dirty };
  }, [dirtyRows, selectedRows, selectAll, rowCount]);

  const hasNewEntities = useMemo(
    () => Object.values(dirtyRows ?? {}).some((row) => row.id === undefined || row.id === null),
    [dirtyRows],
  );

  const isDeleteButtonSelectionModeActive =
    onDelete !== undefined &&
    ((selectionCount.local > 0 && selectionCount.remote === 0 && selectionCount.dirty === 0) ||
      (!hasNewEntities && selectionCount.dirty === 0 && selectionCount.remote > 0));

  const isProcessButtonSelectionModeActive =
    typeComposant === "DATATABLE"
      ? !hasNewEntities && selectionCount.dirty === 0 && selectionCount.remote > 0
      : true;

  const isSelectionModeActive = selectedRows.length > 0 || selectAll;

  const showSelectionWarningMessage =
    isSelectionModeActive &&
    !isDeleteButtonSelectionModeActive &&
    !isProcessButtonSelectionModeActive;

  useEffect(() => {
    // on passe les colonnes disponibles pour l'export
    setExportProps({
      available: columns
        .filter((c: any) => !c.compoVisible)
        .map(
          (c) =>
            ({
              id: c.id,
              column: c.column,
              label: c.label,
              typeCompo: c.typeCompo,
              joinTableName: c.joinTableName,
            }) as any,
        ),
      chosen: columns
        .filter((c: any) => c.compoVisible)
        .map(
          (c) =>
            ({
              id: c.id,
              column: c.column,
              label: c.label,
              typeCompo: c.typeCompo,
              joinTableName: c.joinTableName,
            }) as any,
        ),
      type: "XLS",
      showExport: false,
      groupByState,
    });
  }, [columns]);

  return (
    <>
      <ProcessusProvider
        sjmoCode={sjmoCode}
        tableName={processOrigine}
        selected={selectedRows}
        dtFilter={lastFilterFetched}
        dtFilterBar={filterBar}
        dtTableName={tableName}
        selectAll={selectAll}
        selectAllType={typeComposant}
        ctrlKey={ctrlKey}
        onAfterSaveProcess={() => {
          refresh(true);
          onAfterSaveProcess && onAfterSaveProcess();
        }}
      >
        <div className={classNames("ag-toolbar-scroll", tw("overflow-x-auto"))}>
          <Toolbar sjmoCode={sjmoCode} tableName={tableName} tableCtrlKey={ctrlkey}>
            <Toolbar.Left>
              <Checkbox
                label=""
                tooltip={t("commun_selectionner_toutes_les_lignes")}
                value={selectAll}
                onValueChange={() => {
                  setSelectAll(!selectAll);
                }}
              />
              <span className={tw("ml-2 mr-3 relative inline-block align-middle")}>
                {selectedNumber} / {rowCount}
              </span>
              {selectedRows.length === 0 && !selectAll && (
                <>
                  {onAdd && groupByState.columns.length === 0 && (
                    <Toolbar.Item>
                      <Field>
                        <Toolbar.Add
                          onAdd={() => onAdd("START")}
                          selectedFocus={selectedFocus ?? undefined}
                        />
                      </Field>
                    </Toolbar.Item>
                  )}
                  <Toolbar.Item>
                    <Field addons={true}>
                      {onSave && (
                        <Toolbar.Save
                          title={t("commun_sauvegarder")}
                          onSave={onSave}
                          isRounded={false}
                          disabled={onSave === undefined}
                        >
                          <IconSave
                            insertable={true}
                            updatatable={true}
                            isDirty={Object.keys(dirtyRows ?? {}).length > 0}
                          />
                        </Toolbar.Save>
                      )}

                      <Toolbar.Refresh onRefresh={() => refresh(false)} />

                      <Toolbar.Satellite
                        renderSatelliteMenu={(
                          isActive: boolean,
                          onClose: () => void,
                          id?: string,
                        ) => {
                          return (
                            <SatelliteMenu
                              tableName={tableName}
                              contextId={id}
                              query={currentFilter}
                              columns={columns.slice(0, 4).map((col) => col.column)}
                              sjmoCode={sjmoCode}
                              isActive={isActive}
                              onClick={() => {
                                onClose();
                                track("datatable::satellite::menu::open");
                              }}
                              onMouseLeave={onClose}
                              ctrlKey={ctrlkey}
                            />
                          );
                        }}
                      />

                      <Menu autoclose>
                        <Menu.Button
                          className={tw(
                            "px-4 py-2 text-blue-600 bg-white hover:bg-blue-50 flex items-center rounded-r-md",
                          )}
                        >
                          <span>
                            <Trans i18nKey="commun_action" />
                          </span>
                          <Fa icon="caret-down" fixedWidth />
                        </Menu.Button>
                        <Menu.Overlay>
                          <Menu.Item className="flex">
                            <div className="flex justify-between pr-8">
                              <div
                                className={tw("cursor-pointer")}
                                onClick={() => {
                                  setGridPadding("small");
                                  track("datatable::gridpadding", { size: "small" });
                                }}
                              >
                                <span
                                  className={classNames("icon", {
                                    "has-text-grey": gridPadding !== "small",
                                  })}
                                  title="small"
                                >
                                  <Fa icon="th" aria-label="size grid small" />
                                </span>
                              </div>
                              <div
                                className={tw("cursor-pointer")}
                                onClick={() => {
                                  setGridPadding("large");
                                  track("datatable::gridpadding", { size: "large" });
                                }}
                              >
                                <span
                                  className={classNames("icon", {
                                    "has-text-grey": gridPadding !== "large",
                                  })}
                                  title="large"
                                >
                                  <Fa icon="th-large" aria-label="size grid small" />
                                </span>
                              </div>
                            </div>
                            <div>
                              <Trans i18nKey="commun_grille" />
                            </div>
                          </Menu.Item>
                          <Menu.Separator />
                          <Menu.Item
                            as="a"
                            onClick={() => {
                              if (isGroupable) {
                                setOpenBreakRowDialog(true);
                                track("datatable::groupBy::open");
                              }
                            }}
                            aria-label="add group by"
                            className={tw(isGroupable ? "" : "opacity-50 cursor-not-allowed")}
                            title={
                              isGroupable ? undefined : "commun_ajouter_un_regroupement_disabled"
                            }
                          >
                            <span className={tw("mr-1")}>
                              <Fa icon="outdent" className={tw("text-blue-600")} fixedWidth />
                            </span>
                            <span>
                              <Trans i18nKey="commun_ajouter_un_regroupement" />
                            </span>
                          </Menu.Item>
                          {typeComposant === "INTERACTIVE_REPORT" && (
                            <>
                              <Menu.Item
                                as="a"
                                onClick={() => setHighlightDialogOpen(true)}
                                aria-label="highlight"
                              >
                                <span className={tw("mr-1")}>
                                  <Fa
                                    icon="highlighter"
                                    className={tw("text-blue-600")}
                                    fixedWidth
                                  />
                                </span>
                                <span>
                                  <Trans i18nKey="commun_highlight">Mise en valeur</Trans>
                                </span>
                              </Menu.Item>

                              <Menu.Item
                                as="a"
                                onClick={() => setAggregateDialogOpen(true)}
                                aria-label="aggregate"
                              >
                                <span className={tw("mr-1")}>
                                  <Fa icon="sigma" className={tw("text-blue-600")} fixedWidth />
                                </span>
                                <span>
                                  <Trans i18nKey="commun_agreger">Agréger</Trans>
                                </span>
                              </Menu.Item>
                            </>
                          )}
                          <Menu.Separator />
                          <Menu.Item
                            as="a"
                            onClick={(e) =>
                              setExportProps({
                                ...exportProps,
                                type: "XLS",
                                showExport: true,
                                groupByState,
                              })
                            }
                          >
                            <span className={tw("mr-1")}>
                              <Fa icon="file-excel" className={tw("text-blue-600")} fixedWidth />
                            </span>
                            <span>
                              <Trans i18nKey="commun_export_excel" />
                            </span>
                          </Menu.Item>

                          <Menu.Item
                            as="a"
                            onClick={(e) =>
                              setExportProps({ ...exportProps, type: "PDF", showExport: true })
                            }
                          >
                            <span className={tw("mr-1")}>
                              <Fa icon="file-pdf" className={tw("text-blue-600")} fixedWidth />
                            </span>
                            <span>
                              <Trans i18nKey="commun_export_pdf" />
                            </span>
                          </Menu.Item>
                        </Menu.Overlay>
                      </Menu>
                    </Field>
                  </Toolbar.Item>
                </>
              )}
              {(selectedRows.length > 0 || selectAll) && (
                <Toolbar.Item>
                  <Field addons={true}>
                    <Toolbar.Delete
                      onDelete={isDeleteButtonSelectionModeActive ? onDelete : undefined}
                      isRounded={false}
                      disabled={!isDeleteButtonSelectionModeActive}
                    />
                    <Toolbar.ProcessMenu disabled={!isProcessButtonSelectionModeActive} />
                    <Toolbar.CancelSelect cancelSelect={() => setSelectAll(false)} />
                  </Field>
                </Toolbar.Item>
              )}
              <Toolbar.FocusMenu
                focus={focus}
                selectedFocus={selectedFocus ?? undefined}
                onFocusChange={onFocusChange}
                onSaveFocusPerso={onSaveFocusPerso}
                onRazFocusPerso={onRazFocusPerso}
              />
              <div className="flex items-center justify-center">
                {filterable && !hideFilterBar && (
                  <>
                    <Menu autoclose>
                      <Menu.Button>
                        <Fa icon="search" />
                      </Menu.Button>
                      <Menu.Overlay>
                        <div className="flex flex-col items-start p-5">
                          <FilterBarComponent
                            filters={filters}
                            selectedFilter={filterBar?.filterBarId ?? null}
                            startDate={filterBar?.startDate ?? null}
                            endDate={filterBar?.endDate ?? null}
                            selectedFilterChange={(e: any) =>
                              handleFilterChange(e.target.value, null, null)
                            }
                            startDateChange={(e: any) =>
                              handleFilterChange(null, e.target.value, null)
                            }
                            endDateChange={(e: any) =>
                              handleFilterChange(null, null, e.target.value)
                            }
                          />
                        </div>
                      </Menu.Overlay>
                    </Menu>
                    {filterBar &&
                      (filterBar.filterBarId || filterBar.startDate || filterBar.endDate) && (
                        <span className={tw("ml-2 relative inline-block align-middle")}>
                          {filters.find((f) => f.filterBarId === filterBar.filterBarId)?.label}
                          {!filterBar.filterBarId && `${t("commun_filtre_sur_dates")}`}
                        </span>
                      )}
                  </>
                )}
                {columnFilter.length > 0 && (
                  <>
                    <span className={tw("ml-3 relative inline-block align-middle")}>
                      <Tooltip
                        portalZindex={31}
                        label={
                          <ul className="text-xs">
                            {columnFilter.map((c) => (
                              <li key={c}>{`- ${c}`}</li>
                            ))}
                          </ul>
                        }
                      >
                        <Button
                          onClick={clearColumnFilter}
                          disabled={!filterable}
                          variant="text"
                          size="sm"
                        >
                          <Fa icon={["fas", "filter"]} />
                        </Button>
                      </Tooltip>
                    </span>
                  </>
                )}
                {filterable && !hideFilterBar && (
                  <Toolbar.FilterAllFields
                    value={filterAllFields}
                    onFilterAllFields={(e: React.FocusEvent<HTMLInputElement>) => {
                      setFilterAllFields(e.target.value ?? undefined);
                    }}
                  />
                )}
              </div>
              <Toolbar.Item>
                {showSelectionWarningMessage ? (
                  <span className="tags has-addons">
                    <span className="tag is-black">{t("commun_attention")}</span>
                    <span className="tag is-white">
                      {t("commun_sauvegarder_avant_mode_selection")}
                    </span>
                  </span>
                ) : null}
              </Toolbar.Item>
            </Toolbar.Left>
          </Toolbar>
        </div>
      </ProcessusProvider>
      {exportProps.showExport === true && (
        <DragNDropContext>
          <SelectFieldsModal
            title="commun_choisir_champs_a_exporter"
            defaultChosenFields={exportProps.chosen}
            allFields={exportProps.available}
            disable={groupByState.columns.length > 0}
            onValidate={(columns: Pojo[], useTrad: boolean) =>
              prepareExportData(
                exportProps.type,
                getColumnListToExport(
                  columns,
                  [...exportProps.available, ...exportProps.chosen],
                  groupByState,
                ),
                [...exportProps.available, ...exportProps.chosen],
                t("commun_au_moins_1_champs_export"),
                sjmoCode,
                tableName,
                currentFilter || "",
                useTrad,
                typeComposant,
                groupByState,
                ctrlKey,
                filterBar,
                sjpaId,
              )
            }
            onClose={() => setExportProps({ ...exportProps, showExport: false })}
          />
        </DragNDropContext>
      )}
      {openBreakRowDialog && (
        <GroupByDialog
          columns={columns}
          initialGroupBy={groupByState.columns}
          initialFunctionList={groupByState.functionList}
          initialDefaultOpenBreakRows={defaultOpenBreakRows}
          useLabel={false}
          onClose={() => setOpenBreakRowDialog(false)}
          onValidate={(newColumns, newFunctionList, nextDefaultOpenBreakRows) => {
            setGroupBy(
              newColumns,
              newFunctionList.map((fn, index) => ({ ...fn, label: index.toString() })),
              nextDefaultOpenBreakRows,
            );
            setOpenBreakRowDialog(false);
          }}
        />
      )}
      {highlightDialogOpen &&
        highlightColumns &&
        onValidateHighlights &&
        onDeleteHighlights &&
        onClearHighlights && (
          <HighlightDialog
            columns={highlightColumns}
            highlights={highlights}
            onValidate={(highlight: HighlightProperties) => {
              onValidateHighlights(highlight);
              setHighlightDialogOpen(false);
            }}
            onClose={() => setHighlightDialogOpen(false)}
            onDelete={(index: number) => {
              onDeleteHighlights(index);
              setHighlightDialogOpen(false);
            }}
            onClear={onClearHighlights}
          />
        )}
      {aggregateDialogOpen && (
        <AggregateDialog
          columns={columnsWithNumber}
          aggregates={aggregates}
          onClose={() => setAggregateDialogOpen(false)}
          onValidate={(aggr: AggregateProperties) => {
            const index = aggregates.findIndex((a) => a.id === aggr.id);
            const next =
              index !== -1
                ? aggregates.map((a, i) => (i === index ? aggr : a))
                : [...aggregates, aggr];
            onChangeAggregates(next);
            setAggregateDialogOpen(false);
          }}
          onDelete={(index: number) => {
            const next = aggregates.filter((_, i) => i !== index);
            onChangeAggregates(next);
          }}
          onClear={() => onChangeAggregates([])}
        />
      )}
    </>
  );
};

const IconSave = ({
  insertable,
  updatatable,
  isDirty,
}: {
  insertable: boolean;
  updatatable: boolean;
  isDirty: boolean;
}) => {
  const isInsertable = insertable ?? true;
  const isUpdatatable = updatatable ?? true;

  if (isInsertable === false && isUpdatatable === true) {
    return (
      <span className={tw("relative inline-block align-middle w-4 h-4")}>
        <Fa
          className={tw("absolute inset-0 text-center", isDirty ? "text-red-600" : "text-blue-600")}
          icon={["fas", "save"]}
          size="1x"
          transform="grow-3"
        />
        <Fa
          className={tw("absolute inset-0 text-center", "text-black")}
          icon={["fas", "pencil"]}
          size="sm"
          transform="down-9 right-8 shrink-2"
        />
      </span>
    );
  } else if (isInsertable === true && isUpdatatable === false) {
    return (
      <span className="relative inline-block align-middle w-4 h-4">
        <Fa
          className={tw("absolute inset-0 text-center", isDirty ? "text-red-600" : "text-blue-600")}
          icon={["fas", "save"]}
          size="1x"
          transform="grow-3"
        />
        <Fa
          className={tw("absolute inset-0 text-center", "text-black")}
          icon={["fad", "plus-circle"]}
          size="sm"
          transform="down-9 right-8 shrink-2"
          style={
            {
              "--fa-primary-color": "white",
              "--fa-secondary-color": "black",
            } as any
          }
        />
      </span>
    );
  }

  return (
    <span className="w-4 h-4">
      <Fa
        icon={["fas", "save"]}
        className={tw(`h-4 w-4 ${isDirty ? "text-red-600" : "text-blue-600"}`)}
      />
    </span>
  );
};

export default AgTableToolbar;
